import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiCalendar, FiMapPin, FiSmartphone, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { database } from '../config/firebase';
import PromoBanner from './PromoBanner';
import { ref, push, serverTimestamp } from 'firebase/database';
// import { useTranslation } from 'react-i18next';

// Comprehensive list of Arab cities in Israel
const arabCities = [
  { en: "Jerusalem", ar: "القدس" },
  { en: "Nazareth", ar: "الناصرة" },
  { en: "Umm al-Fahm", ar: "أم الفحم" },
  { en: "Rahat", ar: "رهط" },
  { en: "Sakhnin", ar: "سخنين" },
  { en: "Arraba", ar: "عرابة" },
  { en: "Shefa-'Amr", ar: "شفاعمرو" },
  { en: "Taibe", ar: "الطيبة" },
  { en: "Tira", ar: "الطيرة" },
  { en: "Kafr Qasim", ar: "كفر قاسم" },
  { en: "Qalansawe", ar: "قلنسوة" },
  { en: "Kafr Kanna", ar: "كفر كنا" },
  { en: "Kafr Manda", ar: "كفر مندا" },
  { en: "Jisr az-Zarqa", ar: "جسر الزرقاء" },
  { en: "Maghar", ar: "المغار" },
  { en: "Tamra", ar: "طمرة" },
  { en: "Deir Hanna", ar: "دير حنا" },
  { en: "Bu'eine Nujeidat", ar: "بعينة نجيدات" },
  { en: "Daburiyya", ar: "دبورية" },
  { en: "I'billin", ar: "اعبلين" },
  { en: "Jatt", ar: "جت" },
  { en: "Yafa an-Naseriyye", ar: "يافة الناصرة" },
  { en: "Ein Mahil", ar: "عين ماهل" },
  { en: "Mashhad", ar: "المشهد" },
  { en: "Reineh", ar: "الرينة" },
  { en: "Shfaram", ar: "شفاعمرو" },
  { en: "Tur'an", ar: "طرعان" },
  { en: "Buq'ata", ar: "بقعاتا" },
  { en: "Majdal Shams", ar: "مجدل شمس" },
  { en: "Mas'ade", ar: "مسعدة" },
  { en: "Ein Qiniyye", ar: "عين قنية" },
  { en: "Kafr Yasif", ar: "كفر ياسيف" },
  { en: "Jaljulia", ar: "جلجولية" },
  { en: "Fureidis", ar: "الفريديس" },
  { en: "Kafr Bara", ar: "كفر برا" },
  { en: "Ar'ara", ar: "عرعرة" },
  { en: "Ar'arat an-Naqab", ar: "عرعرة النقب" },
  { en: "Hura", ar: "حورة" },
  { en: "Lakiya", ar: "اللقية" },
  { en: "Shaqib al-Salam", ar: "شقيب السلام" },
  { en: "Tel as-Sabi", ar: "تل السبع" },
  { en: "Kuseife", ar: "كسيفة" },
  { en: "Tuba-Zangariyye", ar: "طوبا الزنغرية" },
  { en: "Yirka", ar: "يركا" },
  { en: "Abu Snan", ar: "أبو سنان" },
  { en: "Deir al-Asad", ar: "دير الأسد" },
  { en: "Majd al-Krum", ar: "مجد الكروم" },
  { en: "Nahf", ar: "نحف" },
  { en: "Sajur", ar: "ساجور" },
  { en: "Bi'ina", ar: "بعينة" },
  { en: "Peki'in", ar: "البقيعة" },
  { en: "Yavne'el", ar: "يفنيئيل" },
  { en: "Other", ar: "أخرى" }
];

const WaitlistSection = styled.section`
  background-color: ${props => props.theme.colors.white};
  position: relative;
  overflow: hidden;
  padding: 100px 0;
`;

const WaitlistContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  position: relative;
  z-index: 1;
  direction: rtl;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.dark};
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 18px;
  text-align: center;
  margin-bottom: 60px;
  max-width: 700px;
  color: ${props => props.theme.colors.dark};
  opacity: 0.8;
`;

const WaitlistContent = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  gap: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
`;

const WaitlistImageWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.07);
  padding: 36px 18px;
  min-height: 450px;
  max-width: 420px;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    max-width: 100%;
    min-height: 220px;
    padding: 18px 0 0 0;
    border-radius: 18px 18px 0 0;
  }
`;

const WaitlistImage = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.09);
`;

const WaitlistCard = styled.div`
  flex: 1.4;
  background: rgba(255,255,255,0.87);
  box-shadow: 0 8px 32px rgba(218,1,92,0.10);
  border-radius: 24px;
  padding: 36px 28px;
  margin-bottom: 2.5rem;
  width: 100%;
  backdrop-filter: blur(6px);
  display: flex;
  flex-direction: column;
  min-width: 340px;
  max-width: 480px;
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    max-width: 100%;
    min-width: 0;
    padding: 32px 12px 24px 12px;
    border-radius: 0 0 18px 18px;
  }
`;

const TabBar = styled.div`
  display: flex;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const TabButton = styled.button<{active: boolean}>`
  flex: 1;
  padding: 16px 0;
  background: ${props => props.active ? props.theme.colors.primary : '#f5f5f5'};
  color: ${props => props.active ? '#ffffff' : props.theme.colors.dark};
  border: none;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const WaitlistInfo = styled.div`
  flex: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin-bottom: 50px;
    text-align: center;
  }
`;

const WaitlistTitle = styled(motion.h3)`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.dark};
`;

const WaitlistDescription = styled(motion.p)`
  font-size: 18px;
  margin-bottom: 30px;
  color: ${props => props.theme.colors.dark};
  opacity: 0.8;
  line-height: 1.6;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  text-align: left;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CheckIcon = styled.span`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.white};
`;

// Add all necessary components
const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  margin-bottom: 20px;
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 24px;
  }
`;

const BenefitTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: ${props => props.theme.colors.dark};
`;

const BenefitDescription = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.gray};
  line-height: 1.5;
`;

const WaitlistForm = styled.div`
  flex: 1;
  background-color: ${props => props.theme.colors.white};
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 30px;
  }
`;

const FormTitle = styled.h4`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 30px;
  color: ${props => props.theme.colors.dark};
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${props => props.theme.colors.dark};
  text-align: right;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.theme.colors.gray}30;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20;
    outline: none;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.theme.colors.gray}30;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  appearance: none;
  background-color: #fff;
  background-image: url('data:image/svg+xml;utf8,<svg fill="%2330D286" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 16px;
  /* Mobile-friendly dropdown: limit height and make scrollable */
  max-height: 48px;
  overflow-y: auto;

  /* Style the dropdown list (when open) for mobile */
  @media (max-width: 600px) {
    font-size: 16px;
    max-height: 44px;
  }

  /* This affects the dropdown list when open */
  & option {
    font-size: 15px;
    padding: 10px 12px;
  }

  /* For mobile browsers, try to keep the dropdown from overflowing */
  &::-webkit-scrollbar {
    width: 6px;
    background: #eee;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 6px;
  }
`;

const FormCheckboxGroup = styled.div`
  margin-top: 25px;
`;

const FormCheckbox = styled.input`
  margin-right: 10px;
`;

const FormCheckboxLabel = styled.label`
  font-size: 14px;
  color: ${props => props.theme.colors.dark};
  line-height: 1.5;
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  background: ${props => props.theme.colors.primary};
  color: #162447; /* Navy blue text on green or white background */
  font-weight: 600;
  border-radius: 8px;
  font-size: 16px;
  margin-top: 20px;
  transition: all 0.3s ease;
  

`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: ${props => props.theme.colors.dark};
  
  a {
    color: ${props => props.theme.colors.primary};
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FleetOwnerLink = styled.div`
  text-align: center;
  margin-top: 30px;
  font-size: 14px;
  color: ${props => props.theme.colors.dark};
  
  a {
    color: ${props => props.theme.colors.primary};
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessMessage = styled(motion.div)`
  background-color: #ffe0f0;
  border: 1px solid #ffb3d9;
  color: #b80149;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessage = styled(motion.div)`
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
`;

const WhyDriveSection = styled.div`
  margin-top: 100px;
  width: 100%;
`;

const WhyDriveTitle = styled.h3`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 50px;
  color: ${props => props.theme.colors.dark};
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 60px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

// New styled component for the main title section
const MainTitleSection = styled.div`
  margin-bottom: 80px;
  text-align: center;
`;

// Update the ContinueButton to be more generic for both continue and back
const FormNavigationButton = styled(motion.button)<{ isPrimary?: boolean }>`
  width: 100%;
  padding: 15px;
  background: ${props => props.isPrimary ? props.theme.colors.primary : props.theme.colors.light};
  color: #162447; /* Navy blue text on green or white background */
  font-weight: 600;
  border-radius: 8px;
  font-size: 16px;
  margin-top: 20px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: ${props => props.isPrimary ? 'none' : `1px solid ${props.theme.colors.dark}20`};
  

`;

// Add a container for the navigation buttons
const FormNavigationContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const DriverWaitlist: React.FC = () => {
  // Tab state
  const [tab, setTab] = useState<'rider' | 'driver'>('driver');
  // Only show driver tab, hide rider

  // Rider form state
  const [riderData, setRiderData] = useState({
    riderContact: '', // phone or email
    riderCity: '',
  });
  const [riderError, setRiderError] = useState('');
  const [riderSuccess, setRiderSuccess] = useState(false);
  const [riderSubmitting, setRiderSubmitting] = useState(false);
  // Step for one-field-at-a-time rider form
  const [riderStep, setRiderStep] = useState(1);

  const handleRiderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRiderData({ ...riderData, [name]: value });
  };

  const handleRiderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRiderError('');
    if (!riderData.riderContact || !riderData.riderCity) {
      setRiderError('يرجى تعبئة جميع الحقول المطلوبة');
      return;
    }
    setRiderSubmitting(true);
    try {
      // Debug: log what will be sent
      console.log('Submitting rider application:', riderData);
      await push(ref(database, 'riderApplications'), {
        riderContact: riderData.riderContact,
        riderCity: riderData.riderCity,
        timestamp: serverTimestamp(),
        submittedAt: new Date().toLocaleString('sv-SE', { timeZoneName: 'short' }),
      });
      setRiderSuccess(true);
      setRiderData({ riderContact: '', riderCity: '' });
      // Debug: log success
      console.log('Rider application submitted successfully');
    } catch (error) {
      setRiderError('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى');
      // Debug: log error
      console.error('Error submitting rider application:', error);
    } finally {
      setRiderSubmitting(false);
    }
  };

  
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    experience: '',
    vehicle: '',
    availability: '',
    fleet: '', // 'yes' or 'no'
    hasLicense: '', // 'yes' or 'no'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phone) {
      setError("يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }
    setStep(2);
    setError('');
  };

  const handleBack = () => {
    setStep(1);
  };
  
  // Driver form submit handler (without city)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.experience || !formData.vehicle || !formData.availability || !formData.fullName || !formData.phone || !formData.fleet || !formData.hasLicense) {
      setError("يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      // Save to Firebase
      await push(ref(database, 'driverApplications'), {
        ...formData,
        fleet: formData.fleet === 'yes',
        hasLicense: formData.hasLicense === 'yes',
        timestamp: serverTimestamp(),
        submittedAt: new Date().toLocaleString('sv-SE', { timeZoneName: 'short' }),
      });
      setSuccess(true);
      setFormData({
        fullName: '',
        phone: '',
        experience: '',
        vehicle: '',
        availability: '',
        fleet: '',
        hasLicense: '',
      });
    } catch (error) {
      setError("حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WaitlistSection id="drivers">
      <PromoBanner />
      <WaitlistContainer>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span style={{ color: '#000' }}>
  سجّل في{' '}
  <span style={{ color: '#DA015C' }}>واصل</span>
</span>
        </SectionTitle>
        

        <WaitlistContent>
          <WaitlistImageWrapper>
            <WaitlistImage src="/images/wassel-driver.jpg" alt="Wassel Driver" />
          </WaitlistImageWrapper>

          <WaitlistCard>
            {/* Rider tab removed - only driver registration */}

            {false ? (
              <form /* Rider registration form - HIDDEN */ onSubmit={handleRiderSubmit}>
                <FormTitle>سجّل كراكب</FormTitle>
                {riderSuccess ? (
  <SuccessMessage
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <FiCheck style={{ fontSize: 48, color: '#DA015C', marginBottom: 20 }} />
    <h4 style={{ marginBottom: 10 }}>شكراً على التسجيل!</h4>
    <p>تم إرسال طلبك بنجاح! سنقوم بالتواصل معك قريبًا.</p>
    <div style={{ marginTop: 30, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        type="button"
        style={{
          width: '100%',
          maxWidth: 340,
          padding: '16px 0',
          background: '#DA015C',
          color: '#fff',
          fontWeight: 700,
          fontSize: 18,
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(48,210,134,0.09)',
          transition: 'background 0.2s',
          margin: '0 auto',
          letterSpacing: 0.5,
        }}
        onClick={() => {
          if (navigator.share) {
            navigator.share({
              title: 'واصل | Wassel',
              text: 'انضم إلى واصل!',
              url: 'https://wassel.taxi',
            });
          } else {
            navigator.clipboard.writeText('https://wassel.taxi');
            alert('تم نسخ الرابط! يمكنك الآن مشاركته مع أصدقائك');
          }
        }}
      >
        شارك مع صديق
      </button>
    </div>
  </SuccessMessage>
) : (
                  <>
                    {riderError && (
                      <ErrorMessage
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {riderError}
                      </ErrorMessage>
                    )}
                    <FormGroup>
                      <FormLabel htmlFor="riderContact">رقم الجوال</FormLabel>
                      <FormInput
                        id="riderContact"
                        name="riderContact"
                        type="tel"
                        value={riderData.riderContact}
                        onChange={handleRiderChange}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel htmlFor="riderCity">المدينة</FormLabel>
                      <FormSelect
                        id="riderCity"
                        name="riderCity"
                        value={riderData.riderCity}
                        onChange={e => setRiderData({ ...riderData, riderCity: e.target.value })}
                        required
                      >
                        <option value="">اختر مدينتك</option>
                        {arabCities.map(city => (
                          <option key={city.en} value={city.en}>{city.ar}</option>
                        ))}
                      </FormSelect>
                    </FormGroup>
                    <FormNavigationContainer>
                      <FormNavigationButton
                        type="submit"
                        isPrimary={true}
                        disabled={riderSubmitting}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {riderSubmitting ? "جاري الإرسال..." : "تسجيل"}
                      </FormNavigationButton>
                    </FormNavigationContainer>
                  </>
                )}
              </form>
            ) : (
              // Driver registration (multi-step, as before)
              <form id="waitlist-form" onSubmit={step === 1 ? handleContinue : handleSubmit}>
                <FormTitle>
  {step === 1 ? "الخطوة الأولى" : "الخطوة الثانية"}
</FormTitle>

                {error && (
                  <ErrorMessage
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {error}
                  </ErrorMessage>
                )}
                <AnimatePresence mode="wait">
                  {step === 1 ? (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FormGroup>
                        <FormLabel htmlFor="fullName">الاسم الكامل</FormLabel>
                        <FormInput
                          id="fullName"
                          name="fullName"
                          type="text"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
  <FormLabel htmlFor="phone">رقم الجوال</FormLabel>
  <FormInput
    id="phone"
    name="phone"
    type="tel"
    value={formData.phone}
    onChange={handleChange}
    required
  />
</FormGroup>

                      
                      
                      <FormNavigationButton
                        type="submit"
                        isPrimary={true}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        استمر <FiArrowRight />
                      </FormNavigationButton>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FormGroup>
                        <FormLabel htmlFor="city">المدينة</FormLabel>
                        <FormSelect
                          id="city"
                          name="city"

                          onChange={handleChange}
                          required
                        >
                          <option value="">اختر مدينتك</option>
                          {arabCities.map(city => (
                            <option key={city.en} value={city.en}>{city.ar}</option>
                          ))}
                        </FormSelect>
                      </FormGroup>
                      
                      <FormGroup>
                        <FormLabel htmlFor="experience">خبرتك كسائق تاكسي</FormLabel>
                        <FormSelect
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          required
                        >
                          <option value="">اختر خبرتك كسائق تاكسي</option>
                          <option value="Less than 1 year">أقل من سنة</option>
                          <option value="1-3 years">من سنة إلى 3 سنوات</option>
                          <option value="3-5 years">من 3 إلى 5 سنوات</option>
                          <option value="5+ years">أكثر من 5 سنوات</option>
                        </FormSelect>
                      </FormGroup>
                      

                      <FormGroup>
                        <FormLabel htmlFor="vehicle">نوع المركبة</FormLabel>
                        <FormSelect
                          id="vehicle"
                          name="vehicle"
                          value={formData.vehicle}
                          onChange={handleChange}
                          required
                        >
                          <option value="">اختر نوع المركبة</option>
                          <option value="4-seater">سيارة ٤ مقاعد</option>
                          <option value="7-seater">سيارة ٧ ركاب</option>
<option value="Minibus">ميني باص (14-20 مقعد)</option>
                          <option value="Motorcycle">دراجة نارية</option>
                          <option value="None (Will use company vehicle)">لا شيء (سأستخدم مركبة الشركة)</option>
                        </FormSelect>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>هل أنت شركة نقل (أسطول) أو مكتب تاكسي؟</FormLabel>
                        <div style={{ display: 'flex', gap: '18px', marginTop: 8 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="radio"
                              name="fleet"
                              value="yes"
                              checked={formData.fleet === 'yes'}
                              onChange={handleChange}
                              required
                            />
                            نعم، شركة نقل/مكتب تاكسي
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="radio"
                              name="fleet"
                              value="no"
                              checked={formData.fleet === 'no'}
                              onChange={handleChange}
                              required
                            />
                            لا، سائق فردي
                          </label>
                        </div>
                      </FormGroup>
                      
                      <FormGroup>
                        <FormLabel htmlFor="availability">الأوقات المتاحة</FormLabel>
                        <FormSelect
                          id="availability"
                          name="availability"
                          value={formData.availability}
                          onChange={handleChange}
                          required
                        >
                          <option value="">اختر الأوقات المتاحة</option>
                          <option value="Full-time">دوام كامل</option>
                          <option value="Part-time">دوام جزئي</option>
                          <option value="Weekends only">أيام الأسبوع فقط</option>
                          <option value="Evenings only">مساءً فقط</option>
                          <option value="Flexible">مرن</option>
                        </FormSelect>
                      </FormGroup>

                      <FormGroup>
                        <FormLabel>هل لديك رخصة قيادة تاكسي سارية؟</FormLabel>
                        <div style={{ display: 'flex', gap: '18px', marginTop: 8 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="radio"
                              name="hasLicense"
                              value="yes"
                              checked={formData.hasLicense === 'yes'}
                              onChange={handleChange}
                              required
                            />
                            نعم
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="radio"
                              name="hasLicense"
                              value="no"
                              checked={formData.hasLicense === 'no'}
                              onChange={handleChange}
                              required
                            />
                            لا
                          </label>
                        </div>
                      </FormGroup>
                      
                      {/* Navigation buttons for step 2 */}
                      <FormNavigationContainer>
                        <FormNavigationButton
                          type="button"
                          isPrimary={false}
                          onClick={handleBack}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <FiArrowLeft /> رجوع
                        </FormNavigationButton>
                        
                        <FormNavigationButton
                          type="submit"
                          isPrimary={true}
                          disabled={isSubmitting}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {isSubmitting ? "جاري الإرسال..." : "إرسال"}
                        </FormNavigationButton>
                      </FormNavigationContainer>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            )}


          </WaitlistCard>

          {/* Share with Friends Banner for Both Forms (OUTSIDE the card) */}
          {tab === 'driver' && !success && (
            <div style={{
              marginTop: 32,
              marginBottom: 8,
              padding: '18px 20px',
              background: '#ffe0f0',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 1px 6px rgba(218,1,92,0.06)',
              fontWeight: 500,
              fontSize: 16,
              color: '#b80149',
              maxWidth: 480,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              <div style={{ marginBottom: 12 }}>شارك مع أصدقائك وساعدنا في نشر الخدمة!</div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  style={{
                    background: '#DA015C',
                    color: '#ffffff', // white for better contrast
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(218,1,92,0.08)', // lighter shadow
                    transition: 'background 0.2s',
                  }}
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'واصل | Wassel',
                        text: 'انضم إلى واصل!',
                        url: 'https://wassel.taxi',
                      });
                    } else {
                      navigator.clipboard.writeText('https://wassel.taxi');
                      alert('تم نسخ الرابط! يمكنك الآن مشاركته مع أصدقائك');
                    }
                  }}
                >
                  مشاركة
                </button>
                <button
                  type="button"
                  style={{
                    background: '#fff',
                    color: '#b80149', // dark pink for better contrast
                    border: '2px solid #DA015C',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(218,1,92,0.08)', // lighter shadow
                    transition: 'background 0.2s',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText('https://wassel.taxi');
                    alert('تم نسخ الرابط! يمكنك الآن مشاركته مع أصدقائك');
                  }}
                >
                  نسخ الرابط
                </button>
              </div>
            </div>
          )}
        </WaitlistContent>
      </WaitlistContainer>
    </WaitlistSection>
  );
};

export default DriverWaitlist;